import type { Operation } from 'fast-json-patch';
import { applyPatch } from 'fast-json-patch';
import { memoize } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import * as zod from 'zod';

import { makeNamespacedLog } from '@sb/log';
import { EventEmitter, wait } from '@sb/utilities';
import type { ListenerType } from '@sb/utilities/src/EventEmitter';
import { API_ENDPOINT, HTTP_API_ENDPOINT, globalCache } from '@sbrc/utils';

import type {
  AssemblyServerState,
  AssemblyMessage,
  AssemblyRunnableOptions,
  AssemblyRunnableInfo,
  PipelineDefinition,
  PipelineParameterOverrides,
  RunnableMessage,
} from './AssemblyState';
import {
  AssemblyMessageType,
  PipelineListResponseSchema,
  RunRequestResponse,
} from './AssemblyState';

export type RunPipelineArgs =
  | {
      pipelineKey: string;
      boardSerialNumber: string;
      parameterOverrides?: PipelineParameterOverrides;
    }
  | { pipelineRunId: string; fromStepIndex?: number };

// Server-side filters for the tools endpoint's session-file dropdown.
export interface AssemblyToolsFilters {
  filterBySerial?: boolean;
  filterType?: boolean;
  filterStatus?: boolean;
}

export const ASSEMBLY_FILTER_PARAM_KEYS = [
  'filterBySerial',
  'filterType',
  'filterStatus',
] as const satisfies ReadonlyArray<keyof AssemblyToolsFilters>;

// Validated at the robby-server (C++) boundary so a wire-format drift fails
// loudly rather than flowing through as mistyped data.
export const RunPipelineResponse = zod.object({
  // Optional because robby-server's to_json(RunPipelineResult) only emits
  // `pipelineRunId` once a run has been created. Failures before run creation
  // (unknown pipelineKey, already-running guard, bad request) return
  // `{ success: false, errors: [...] }` with no id; a step-launch failure on a
  // created run returns the id alongside `success: false` — the FAILED run is
  // retained server-side and resumable.
  pipelineRunId: zod.string().optional(),
  success: zod.boolean(),
  errors: zod.array(zod.string()).optional(),
});

export type RunPipelineResponse = zod.infer<typeof RunPipelineResponse>;

const log = makeNamespacedLog('AssemblyServerHandle');
const MAX_MESSAGE_BUFFER = 2048;

/**
 * The set of top-level `AssemblyServerState` keys a JSON-Patch touched, or
 * `null` when the patch replaces the whole document (the periodic full-state
 * snapshot and the initial-connect payload both use a root `""` path).
 *
 * robby-server streams JSON-Patch deltas at 10 Hz, but almost every tick only
 * touches one branch (usually `jointStates`). Emitting every state key on
 * every tick — as the handler used to — forced every React subscriber to
 * re-render even when its slice was untouched. Restricting the fan-out to the
 * keys the patch actually changed is behaviour-preserving: a listener only
 * needs to fire when its data changed.
 */
function changedTopLevelKeys(ops: Operation[]): Set<string> | null {
  const keys = new Set<string>();

  for (const op of ops) {
    // `move`/`copy` ops read from a second location, so a change there can
    // affect that branch's top-level key too.
    const paths =
      'from' in op && typeof op.from === 'string'
        ? [op.path, op.from]
        : [op.path];

    for (const path of paths) {
      // A root-level op ("" or "/") replaces the entire document.
      if (path === '' || path === '/') return null;

      // JSON Pointer: "/topKey/rest…" → "topKey". Unescape per RFC 6901
      // (~1 → "/", ~0 → "~"); top-level keys are plain identifiers today, but
      // unescaping keeps this correct if that ever changes.
      const segment = path.split('/')[1];
      if (segment === undefined) return null;

      keys.add(segment.replace(/~1/g, '/').replace(/~0/g, '~'));
    }
  }

  return keys;
}

export class AssemblyServerHandle {
  private ws: WebSocket | null = null;

  private serverState: AssemblyServerState = {
    desireLoopEnabled: true,
    connectedDevices: {},
    jointStates: {},
    connectedArm: undefined,
    runnableStates: {},
    pipelineRuns: {},
    latestRunsBySerial: {},
    cloudUploadTasks: {},
    dcinVoltage: undefined,
    dcinCurrent: undefined,
    vbusVoltage: undefined,
    buildId: '',
    buildName: '',
    isStableBuild: false,
    isEStopHeld: false,
    isHardwareEStopActive: false,
    powerCommandPending: undefined,
    powerCommandFailed: undefined,
    supportsDirectionalPower: undefined,
    peripheralStates: {},
    canInterfaces: [],
  };

  private connectionStatus = new BehaviorSubject<{ kind: string }>({
    kind: 'constructing',
  });

  private assemblyStateUpdate = new EventEmitter<AssemblyServerState>();

  private resultOutputUpdate = new EventEmitter<{ message: RunnableMessage }>();

  private runnableOutput = new EventEmitter<{
    runnableOutput: {
      runnableKey: string;
      runId: string;
      message: string;
    };
  }>();

  private resultBuffer: RunnableMessage[] = [];

  public constructor() {
    this.connect();
  }

  public listenForStateUpdates<K extends keyof AssemblyServerState>(
    event: K,
    callback: ListenerType<AssemblyServerState, K>,
  ): () => void {
    return this.assemblyStateUpdate.on(event, callback);
  }

  public listenForRunnableOutput(
    callback: ListenerType<
      {
        runnableOutput: {
          runnableKey: string;
          runId: string;
          message: string;
        };
      },
      'runnableOutput'
    >,
  ): () => void {
    return this.runnableOutput.on('runnableOutput', callback);
  }

  public listenForResults(
    callback: ListenerType<
      {
        message: RunnableMessage;
      },
      'message'
    >,
  ): () => void {
    return this.resultOutputUpdate.on('message', callback);
  }

  public getState<K extends keyof AssemblyServerState>(event: K) {
    return this.serverState[event];
  }

  public async getRunnables(
    filters?: AssemblyToolsFilters,
    signal?: AbortSignal,
  ): Promise<AssemblyRunnableInfo[]> {
    const query = new URLSearchParams();
    if (filters?.filterBySerial != null)
      query.append('filterBySerial', String(filters.filterBySerial));
    if (filters?.filterType != null)
      query.append('filterType', String(filters.filterType));
    if (filters?.filterStatus != null)
      query.append('filterStatus', String(filters.filterStatus));

    const queryStr = query.toString();

    const url = `${HTTP_API_ENDPOINT}/robby-server-api/tools/${
      queryStr ? `?${queryStr}` : ''
    }`;

    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal,
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(
        `Failed to load Tool Info from robby-server: ${response.status} ${message}`,
      );
    }

    return response.json();
  }

  public getResentResults(): RunnableMessage[] {
    if (this.resultBuffer.length === 0) return [];

    return this.resultBuffer.slice(Math.max(0, this.resultBuffer.length - 20));
  }

  public clearResults() {
    this.resultBuffer = [];
  }

  public async execute(
    runnable: AssemblyRunnableOptions,
  ): Promise<RunRequestResponse> {
    const response = await fetch(`${HTTP_API_ENDPOINT}/robby-server-api/run/`, {
      method: 'POST',
      body: JSON.stringify(runnable),
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const message = await response.text();

      return {
        key: runnable.key,
        runId: '',
        success: false,
        errors: [
          `Execute failed: Received non-ok response from robby-server: ${response.status} ${message}`,
        ],
      };
    }

    const responseJson: any = await response.json();

    return RunRequestResponse.parse(responseJson);
  }

  public async fetchPipelines(): Promise<PipelineDefinition[]> {
    const response = await fetch(
      `${HTTP_API_ENDPOINT}/robby-server-api/pipelines/`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      },
    );

    if (!response.ok) {
      const message = await response.text();
      throw new Error(
        `Failed to load pipelines from robby-server: ${response.status} ${message}`,
      );
    }

    const body = PipelineListResponseSchema.parse(await response.json());

    return body.pipelines;
  }

  public async runPipeline(
    args: RunPipelineArgs,
  ): Promise<RunPipelineResponse> {
    // Discriminate on the fresh-start fields (`pipelineKey`) rather than on the
    // presence of a `pipelineRunId` key, so a resume payload that somehow
    // carries an undefined id is caught explicitly below instead of being sent
    // to the server with a missing id.
    let body: Record<string, string | number | PipelineParameterOverrides>;

    if ('pipelineKey' in args) {
      body = {
        pipelineKey: args.pipelineKey,
        boardSerialNumber: args.boardSerialNumber,
      };

      if (
        args.parameterOverrides &&
        Object.keys(args.parameterOverrides).length > 0
      ) {
        body.parameterOverrides = args.parameterOverrides;
      }
    } else {
      if (!args.pipelineRunId) {
        throw new Error('runPipeline: resume requires a pipelineRunId');
      }

      body = { pipelineRunId: args.pipelineRunId };

      if (args.fromStepIndex !== undefined) {
        body.fromStepIndex = args.fromStepIndex;
      }
    }

    const response = await fetch(
      `${HTTP_API_ENDPOINT}/robby-server-api/run-pipeline/`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      },
    );

    if (!response.ok) {
      const message = await response.text();
      throw new Error(
        `Failed to run pipeline via robby-server: ${response.status} ${message}`,
      );
    }

    return RunPipelineResponse.parse(await response.json());
  }

  public estop(): string | null {
    if (
      this.ws?.readyState !== WebSocket.OPEN ||
      this.getConnectionStatus().kind !== 'connected'
    ) {
      return 'Cannot ESTOP: Disconnected from server.';
    }

    const stopMessage: AssemblyMessage<AssemblyMessageType.EStop> = {
      type: AssemblyMessageType.EStop,
      payload: null,
    };

    try {
      this.ws?.send(JSON.stringify(stopMessage));
    } catch (err) {
      return `Failed to send ESTOP command: ${err.message ?? 'Unkown Reason'}`;
    }

    return null;
  }

  /**
   * Ask the server to end in a specific power state, rather than toggling
   * whatever it currently believes. Only available against a server that
   * advertises `supportsDirectionalPower`; callers own the legacy path, since
   * choosing between `estop()` and `powerCycle()` needs the state they render
   * from. Falling back to the bare toggle here would let a `'start'` engage an
   * estop against a server that disagrees about the current state — the exact
   * ambiguity this method exists to remove.
   */
  public setPower(target: 'stop' | 'start'): string | null {
    if (this.serverState.supportsDirectionalPower !== true) {
      return 'Cannot change power: server does not support directional commands.';
    }

    if (
      this.ws?.readyState !== WebSocket.OPEN ||
      this.getConnectionStatus().kind !== 'connected'
    ) {
      return 'Cannot change power: Disconnected from server.';
    }

    const type =
      target === 'stop'
        ? AssemblyMessageType.StopPower
        : AssemblyMessageType.StartPower;

    try {
      this.ws?.send(JSON.stringify({ type, payload: null }));
    } catch (err) {
      return `Failed to send ${type} command: ${err.message ?? 'Unkown Reason'}`;
    }

    return null;
  }

  public powerCycle(): string | null {
    if (
      this.ws?.readyState !== WebSocket.OPEN ||
      this.getConnectionStatus().kind !== 'connected'
    ) {
      return 'Cannot Power Cycle: Disconnected from server.';
    }

    const powerCycleMessage: AssemblyMessage<AssemblyMessageType.PowerCycle> = {
      type: AssemblyMessageType.PowerCycle,
      payload: null,
    };

    try {
      this.ws?.send(JSON.stringify(powerCycleMessage));
    } catch (err) {
      return `Failed to send Power Cycle command: ${err.message ?? 'Unknown Reason'}`;
    }

    return null;
  }

  public kill(serialNumber?: number): string | null {
    if (
      this.ws?.readyState !== WebSocket.OPEN ||
      this.getConnectionStatus().kind !== 'connected'
    ) {
      log.error(
        `robby-server-handler`,
        'Trying to kill process but socket is closed.',
      );

      return 'Cannot Stop: Disconnected from server.';
    }

    const stopMessage: AssemblyMessage<AssemblyMessageType.Stop> = {
      type: AssemblyMessageType.Stop,
      payload: serialNumber != null ? { serialNumber } : null,
    };

    try {
      this.ws?.send(JSON.stringify(stopMessage));
    } catch (err) {
      return `Failed to send STOP command: ${err.message ?? 'Unkown Reason'}`;
    }

    return null;
  }

  public removeDevice(serialNumber: number): string | null {
    if (
      this.ws?.readyState !== WebSocket.OPEN ||
      this.getConnectionStatus().kind !== 'connected'
    ) {
      return 'Cannot Remove Device: Disconnected from server.';
    }

    const removeMessage: AssemblyMessage<AssemblyMessageType.Remove> = {
      type: AssemblyMessageType.Remove,
      payload: { serialNumber },
    };

    try {
      this.ws?.send(JSON.stringify(removeMessage));
    } catch (err) {
      return `Failed to send REMOVE command: ${err.message ?? 'Unkown Reason'}`;
    }

    return null;
  }

  public sendUpdate(data: { [key: string]: any }): string | null {
    if (
      this.ws?.readyState !== WebSocket.OPEN ||
      this.getConnectionStatus().kind !== 'connected'
    ) {
      return 'Cannot Send Update: Disconnected from server.';
    }

    const updateMessage: AssemblyMessage<AssemblyMessageType.SetConfig> = {
      type: AssemblyMessageType.SetConfig,
      payload: data,
    };

    try {
      this.ws?.send(JSON.stringify(updateMessage));
    } catch (err) {
      return `Failed to send SET_CONFIG command: ${err.message ?? 'Unkown Reason'}`;
    }

    return null;
  }

  public sendReboot(): string | null {
    if (
      this.ws?.readyState !== WebSocket.OPEN ||
      this.getConnectionStatus().kind !== 'connected'
    ) {
      return 'Cannot Send Reboot: Disconnected from server.';
    }

    const rebootMessage: AssemblyMessage<AssemblyMessageType.Reboot> = {
      type: AssemblyMessageType.Reboot,
      payload: null,
    };

    try {
      this.ws?.send(JSON.stringify(rebootMessage));
    } catch (err) {
      return `Failed to send REBOOT command: ${err.message ?? 'Unkown Reason'}`;
    }

    return null;
  }

  /**
   * Ask robby-server to re-broadcast a full-state snapshot on the
   * `state-update` stream. robby-server no longer pushes a periodic snapshot,
   * so this is the manual resync path (the header refresh button) for the rare
   * case where a client suspects it has drifted from the server's state.
   */
  public requestFullState(): string | null {
    if (
      this.ws?.readyState !== WebSocket.OPEN ||
      this.getConnectionStatus().kind !== 'connected'
    ) {
      return 'Cannot refresh: Disconnected from server.';
    }

    const refreshMessage: AssemblyMessage<AssemblyMessageType.RequestFullState> =
      {
        type: AssemblyMessageType.RequestFullState,
        payload: null,
      };

    try {
      this.ws?.send(JSON.stringify(refreshMessage));
    } catch (err) {
      return `Failed to send REQUEST_FULL_STATE command: ${
        err.message ?? 'Unknown Reason'
      }`;
    }

    return null;
  }

  public sendTriggerDataSync(serialNumber: number): string | null {
    if (
      this.ws?.readyState !== WebSocket.OPEN ||
      this.getConnectionStatus().kind !== 'connected'
    ) {
      return 'Cannot Send Trigger Data Sync: Disconnected from server.';
    }

    const rebootMessage: AssemblyMessage<AssemblyMessageType.TriggerDataSync> =
      {
        type: AssemblyMessageType.TriggerDataSync,
        payload: { serialNumber },
      };

    try {
      this.ws?.send(JSON.stringify(rebootMessage));
    } catch (err) {
      return `Failed to trigger data sync command: ${err.message ?? 'Unkown Reason'}`;
    }

    return null;
  }

  public sendRetryUpload(runId: string): string | null {
    if (
      this.ws?.readyState !== WebSocket.OPEN ||
      this.getConnectionStatus().kind !== 'connected'
    ) {
      return 'Cannot Send Retry Upload: Disconnected from server.';
    }

    const retryUploadMessage: AssemblyMessage<AssemblyMessageType.RetryUpload> =
      {
        type: AssemblyMessageType.RetryUpload,
        payload: { runId },
      };

    try {
      this.ws?.send(JSON.stringify(retryUploadMessage));
    } catch (err) {
      return `Failed to send RETRY_UPLOAD command: ${err.message ?? 'Unkown Reason'}`;
    }

    return null;
  }

  private connect() {
    // don't do anything when running on server
    if (typeof window === 'undefined') {
      return;
    }

    log.info(`uncategorized`, 'Connecting...');
    this.connectionStatus.next({ kind: 'connecting' });

    this.ws = new WebSocket(`${API_ENDPOINT}robby-server-api/manage/`);
    this.ws.binaryType = 'arraybuffer';

    const connectingTimeoutID = setTimeout(() => {
      if (this.ws?.readyState === WebSocket.CONNECTING) {
        log.info(`uncategorized`, 'Timed out while connecting');
        this.ws.close(4001, 'Connecting timeout');
      }
    }, 5_000);

    const destructors: Array<() => void> = [];

    // teardown is memoized so it is only called once
    const teardownAndReconnect = memoize(async () => {
      log.info(`uncategorized`, 'Teardown', {
        connectionState: this.ws?.readyState,
      });

      this.ws?.close();

      for (const destructor of destructors) {
        destructor();
      }

      if (this.getConnectionStatus().kind !== 'connecting') {
        this.connectionStatus.next({ kind: 'disconnected' });
      }

      await wait(1000);
      this.connect();
    });

    this.ws.onopen = () => {
      log.info(`uncategorized`, 'Assembly Server Connected');
      clearTimeout(connectingTimeoutID);
      this.connectionStatus.next({ kind: 'connected' });
    };

    this.ws.onmessage = (event) => {
      try {
        const enc = new TextDecoder();
        const decodedData = enc.decode(event.data);
        const message = JSON.parse(decodedData);
        this.handleAssemblyMessage(message);
      } catch (err) {
        log.error(`uncategorized`, 'Failed to parse', err);
      }
    };

    this.ws.onerror = (event) => {
      log.error(`uncategorized`, 'WS ERROR', event);
    };

    this.ws.onclose = (event) => {
      log.error(`uncategorized`, 'WS CLOSE', event);
      this.connectionStatus.next({ kind: 'disconnected' });
      // Whether the server verifies power commands — and whether its bus
      // voltage was ever measured — is a per-connection capability, inferred
      // from these fields being present. A server that doesn't implement them
      // sends no patch for those paths, so without this reset a reconnect onto
      // an older robby would keep the capability latched from the previous one.
      this.serverState.powerCommandPending = undefined;
      this.serverState.powerCommandFailed = undefined;
      this.serverState.supportsDirectionalPower = undefined;
      this.serverState.vbusReported = undefined;
      this.assemblyStateUpdate.emit('powerCommandPending', undefined);
      this.assemblyStateUpdate.emit('powerCommandFailed', undefined);
      this.assemblyStateUpdate.emit('supportsDirectionalPower', undefined);
      this.assemblyStateUpdate.emit('vbusReported', undefined);
      teardownAndReconnect();
    };
  }

  private handleAssemblyMessage<T extends AssemblyMessageType>(
    message: AssemblyMessage<T>,
  ) {
    switch (message.type) {
      case AssemblyMessageType.Update: {
        let changedKeys: Set<string> | null;

        try {
          const ops = message.payload as Operation[];

          const result = applyPatch(this.serverState, ops, false, false);

          this.serverState = result.newDocument;
          changedKeys = changedTopLevelKeys(ops);
        } catch (_err) {
          break;
        }

        // Only fan out the slices this patch actually touched (null = a
        // whole-document replace, so everything is fresh). This keeps each
        // React subscriber from re-rendering on ticks that don't change its
        // data — the 10 Hz stream almost always touches only `jointStates`.
        const didChange = (key: keyof AssemblyServerState): boolean =>
          changedKeys === null || changedKeys.has(key);

        if (didChange('desireLoopEnabled')) {
          this.assemblyStateUpdate.emit(
            'desireLoopEnabled',
            this.serverState.desireLoopEnabled,
          );
        }

        if (didChange('connectedDevices')) {
          this.assemblyStateUpdate.emit(
            'connectedDevices',
            this.serverState.connectedDevices,
          );
        }

        if (didChange('jointStates')) {
          this.assemblyStateUpdate.emit(
            'jointStates',
            this.serverState.jointStates,
          );
        }

        if (didChange('connectedArm')) {
          this.assemblyStateUpdate.emit(
            'connectedArm',
            this.serverState.connectedArm,
          );
        }

        if (didChange('runnableStates')) {
          this.assemblyStateUpdate.emit(
            'runnableStates',
            this.serverState.runnableStates,
          );
        }

        if (didChange('cloudUploadTasks')) {
          this.assemblyStateUpdate.emit(
            'cloudUploadTasks',
            this.serverState.cloudUploadTasks,
          );
        }

        if (didChange('dcinVoltage')) {
          this.assemblyStateUpdate.emit(
            'dcinVoltage',
            this.serverState.dcinVoltage,
          );
        }

        if (didChange('dcinCurrent')) {
          this.assemblyStateUpdate.emit(
            'dcinCurrent',
            this.serverState.dcinCurrent,
          );
        }

        if (didChange('vbusVoltage')) {
          this.assemblyStateUpdate.emit(
            'vbusVoltage',
            this.serverState.vbusVoltage,
          );
        }

        this.assemblyStateUpdate.emit(
          'vbusReported',
          this.serverState.vbusReported,
        );

        // Emit buildId if available
        if (didChange('buildId') && this.serverState.buildId) {
          this.assemblyStateUpdate.emit('buildId', this.serverState.buildId);
        }

        // Emit buildName if available
        if (didChange('buildName') && this.serverState.buildName) {
          this.assemblyStateUpdate.emit(
            'buildName',
            this.serverState.buildName,
          );
        }

        // Emit isStableBuild if available
        if (
          didChange('isStableBuild') &&
          this.serverState.isStableBuild !== undefined
        ) {
          this.assemblyStateUpdate.emit(
            'isStableBuild',
            this.serverState.isStableBuild,
          );
        }

        // Emit isEStopHeld if available
        if (
          didChange('isEStopHeld') &&
          this.serverState.isEStopHeld !== undefined
        ) {
          this.assemblyStateUpdate.emit(
            'isEStopHeld',
            this.serverState.isEStopHeld,
          );
        }

        // Emit isHardwareEStopActive if available
        if (
          didChange('isHardwareEStopActive') &&
          this.serverState.isHardwareEStopActive !== undefined
        ) {
          this.assemblyStateUpdate.emit(
            'isHardwareEStopActive',
            this.serverState.isHardwareEStopActive,
          );
        }

        if (didChange('powerCommandPending')) {
          this.assemblyStateUpdate.emit(
            'powerCommandPending',
            this.serverState.powerCommandPending,
          );
        }

        if (didChange('powerCommandFailed')) {
          this.assemblyStateUpdate.emit(
            'powerCommandFailed',
            this.serverState.powerCommandFailed,
          );
        }

        if (didChange('supportsDirectionalPower')) {
          this.assemblyStateUpdate.emit(
            'supportsDirectionalPower',
            this.serverState.supportsDirectionalPower,
          );
        }

        if (didChange('pipelineRuns')) {
          this.assemblyStateUpdate.emit(
            'pipelineRuns',
            this.serverState.pipelineRuns,
          );
        }

        if (didChange('latestRunsBySerial')) {
          this.assemblyStateUpdate.emit(
            'latestRunsBySerial',
            this.serverState.latestRunsBySerial,
          );
        }

        if (didChange('peripheralStates')) {
          this.assemblyStateUpdate.emit(
            'peripheralStates',
            this.serverState.peripheralStates,
          );
        }

        if (didChange('canInterfaces')) {
          this.assemblyStateUpdate.emit(
            'canInterfaces',
            this.serverState.canInterfaces,
          );
        }

        // Emitted unconditionally: `null` (firmware-bot-cpp unreachable) is a
        // meaningful value that must propagate to listeners.
        this.assemblyStateUpdate.emit('stLink', this.serverState.stLink);

        this.assemblyStateUpdate.emit(
          'desiresSource',
          this.serverState.desiresSource,
        );

        break;
      }
      case AssemblyMessageType.ResultOnJoint: {
        this.resultBuffer.push(message.payload as RunnableMessage);

        if (this.resultBuffer.length > MAX_MESSAGE_BUFFER) {
          this.resultBuffer = this.resultBuffer.slice(
            this.resultBuffer.length / 2,
          );
        }

        this.resultOutputUpdate.emit(
          'message',
          message.payload as RunnableMessage,
        );

        break;
      }
      default:
        log.info(
          `uncategorized`,
          `Undandled message type: '${message.type}'`,
          message.payload,
        );
    }
  }

  public getConnectionStatus(): { kind: string } {
    return this.connectionStatus.value;
  }

  public onConnectionChange(
    cb: (connectionStatus: { kind: string }) => void,
  ): () => void {
    const subscription = this.connectionStatus.subscribe(cb);

    return () => subscription.unsubscribe();
  }
}

export function getAssemblyServerHandle(): AssemblyServerHandle {
  return globalCache(`robbyServerHandle`, () => {
    const websocketConnection = new AssemblyServerHandle();

    return websocketConnection;
  });
}
